import React, { useCallback, useEffect, useState } from 'react';
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  ReferenceField,
  TextField,
  BooleanField,
  SelectInput,
  DateInput,
  Identifier,
  useDataProvider,
  useNotify,
} from 'react-admin';
import {
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Aftercare, CremationStatus, Funeral } from '../types';
import { AftercareCall1Field } from '../aftercare/AftercareCall1Field';
import { getDefaultAftercareMessage, getAftercareEmailSubject } from '../aftercare/aftercareEmailCopy';
import { getToken } from '../authentication/authProvider';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

const aftercareFilters = [
  <SelectInput
    source="region"
    choices={[
      { id: 'ontario', name: 'Ontario' },
      { id: 'quebec', name: 'Quebec' },
    ]}
    alwaysOn
    emptyText="All regions"
  />,
  <DateInput source="delivered_at@gte" label="Delivered after" alwaysOn />,
  <DateInput source="delivered_at@lte" label="Delivered before" />,
  <SelectInput
    source="aftercare_email_sent"
    label="Email sent"
    choices={[
      { id: 'true', name: 'Sent' },
      { id: 'false', name: 'Not sent' },
    ]}
    emptyText="All"
    alwaysOn
  />,
  <SelectInput
    source="aftercare_call_1_status"
    label="Call 1"
    choices={[
      { id: 'pending', name: 'Pending' },
      { id: 'completed', name: 'Done' },
      { id: 'declined', name: 'Declined' },
    ]}
    emptyText="All"
    alwaysOn
  />,
];

export const AftercareList = () => {
  return (
    <List
      resource="funerals"
      hasCreate={false}
      filters={aftercareFilters}
      filterDefaultValues={{
        cremation_status: CremationStatus.DELIVERED,
        'archived_at@is': 'null',
        'delivered_at@not.is': 'null',
      }}
      sort={{ field: 'delivered_at', order: 'DESC' }}
      perPage={50}
      storeKey="aftercare-list-v1"
      title="Aftercare"
    >
      <Datagrid
        rowClick={(id: Identifier) => `/funerals/${id}/show`}
        bulkActionButtons={false}
        rowStyle={aftercareRowStyle}
      >
        <FunctionField
          label="Id"
          render={(record: Funeral | undefined) => {
            if (!record) return '';
            const suffix =
              record.region === 'ontario'
                ? 'ON'
                : record.region === 'quebec'
                  ? 'QC'
                  : '';
            return `${record.id} ${suffix}`;
          }}
        />
        <ReferenceField
          label="Deceased"
          source="deceased_client_id"
          reference="clients"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Primary Client"
          source="primary_client_id"
          reference="clients"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <TextField source="primary_client_relationship" label="Relationship" sortable={false} />
        <TextField source="language" label="Language" sortable={false} />
        <ReferenceField
          label="Phone"
          source="primary_client_id"
          reference="clients"
          link={false}
          sortable={false}
        >
          <TextField source="phone_number" />
        </ReferenceField>
        <DateField
          label="Delivered"
          source="delivered_at"
          options={{ month: 'short', day: 'numeric', year: 'numeric' }}
        />
        <BooleanField source="has_visitation" label="Visitation" sortable={false} />
        <FunctionField
          label="Call 1"
          sortable={false}
          render={(record: Funeral | undefined) =>
            record ? <AftercareCall1Field funeralId={Number(record.id)} /> : null
          }
        />
        <FunctionField
          label="Send Email"
          sortable={false}
          render={(record: Funeral | undefined) =>
            record ? <SendAftercareEmailButton funeral={record} /> : null
          }
        />
      </Datagrid>
    </List>
  );
};

const aftercareRowStyle = (record: Funeral | undefined) => {
  if (!record) return {};
  if (record.region === 'ontario') {
    return { backgroundColor: '#e8f0fe' };
  }
  return {};
};

const formatSentDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const SendAftercareEmailButton = ({ funeral }: { funeral: Funeral }) => {
  const [open, setOpen] = useState(false);
  const [optimisticSentAt, setOptimisticSentAt] = useState<string | null>(null);

  const sentAt = optimisticSentAt ?? funeral.aftercare_email_sent_at ?? null;
  const tooltip = sentAt ? `Sent ${formatSentDate(sentAt)} — click to resend` : 'Send aftercare email now';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={(e) => e.stopPropagation()}>
      {sentAt && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#2e7d32', fontSize: 12 }}>
          <CheckCircleIcon sx={{ fontSize: 16 }} />
          {formatSentDate(sentAt)}
        </span>
      )}
      <Tooltip title={tooltip}>
        <span>
          <IconButton size="small" onClick={() => setOpen(true)} color="primary">
            <SendIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {open && (
        <SendAftercareEmailDialog
          funeral={funeral}
          onClose={() => setOpen(false)}
          onSent={() => setOptimisticSentAt(new Date().toISOString())}
        />
      )}
    </div>
  );
};

interface RecipientInfo {
  id: number;
  name: string;
  email: string;
}

const SendAftercareEmailDialog = ({
  funeral,
  onClose,
  onSent,
}: {
  funeral: Funeral;
  onClose: () => void;
  onSent: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [recipient, setRecipient] = useState<RecipientInfo | null>(null);
  const [aftercareId, setAftercareId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const funeralId = Number(funeral.id);
        const { data: list } = await dataProvider.getList<Aftercare>('aftercare', {
          filter: { funeral_id: funeralId },
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'ASC' },
        });
        const existing = list[0];
        const recipientId = existing?.email_recipient_client_id ?? Number(funeral.primary_client_id);
        if (!recipientId) {
          if (!cancelled) setError('No recipient client is set on this funeral.');
          return;
        }
        const { data: client } = await dataProvider.getOne('clients', { id: recipientId });
        if (cancelled) return;
        const firstName = client.name?.split(' ')[0] || '';
        setRecipient({ id: client.id, name: client.name || '', email: client.email || '' });
        setAftercareId(existing?.id ?? null);
        setMessage(existing?.email_message || getDefaultAftercareMessage(funeral.language, firstName));
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load aftercare draft');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dataProvider, funeral.id, funeral.language, funeral.primary_client_id]);

  const handleSend = async () => {
    if (!recipient || !recipient.email) {
      notify('Recipient has no email address', { type: 'error' });
      return;
    }
    setSending(true);
    try {
      if (aftercareId) {
        await dataProvider.update('aftercare', {
          id: aftercareId,
          data: {
            email_message: message,
            email_recipient_client_id: recipient.id,
          },
          previousData: { id: aftercareId },
        });
      } else {
        const { data } = await dataProvider.create<Aftercare>('aftercare', {
          data: {
            funeral_id: Number(funeral.id),
            email_enabled: true,
            email_message: message,
            email_recipient_client_id: recipient.id,
            email_scheduled_date: new Date().toISOString(),
          },
        });
        setAftercareId(data.id ?? null);
      }

      const token = await getToken();
      const res = await fetch(`${baseUrl}/aftercare/${funeral.id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await res.json();
      if (res.ok && payload.status === 'success') {
        notify('Aftercare email sent', { type: 'success' });
        onSent();
        onClose();
      } else {
        notify(payload.message || 'Failed to send aftercare email', { type: 'error' });
      }
    } catch (err: any) {
      notify(err.message || 'Failed to send aftercare email', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth onClick={stop}>
      <DialogTitle>Send Aftercare Email</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              To: <strong>{recipient?.name}</strong>{recipient?.email ? ` <${recipient.email}>` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Subject: {getAftercareEmailSubject(funeral.language)}
            </Typography>
            <MuiTextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={10}
              fullWidth
              disabled={sending}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose} disabled={sending}>
          Cancel
        </MuiButton>
        <MuiButton
          variant="contained"
          onClick={handleSend}
          disabled={loading || sending || !message.trim() || !recipient?.email || !!error}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        >
          {sending ? 'Sending…' : 'Send'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default AftercareList;
